import { useState } from 'react';
import { toast } from 'sonner';
import { devProgressAction, devProgressPending } from '@shared/dev-servers';
import { confirmDialog } from '@/components/ConfirmDialog';
import { useLoader } from '@/hooks/use-loader';
import { useNow } from '@/hooks/use-now';
import { useTaskStore } from '@/stores/taskStore';
import { DEV_BUSY_POLL_MS } from './use-task-dev-servers';
import { describeApiError } from '@/lib/api-errors';
import type { ApiResponse } from '@/lib/api-contract';
import { listDevServers, startDevServers, stopDevServers, tidyWorktree } from '@/lib/dev-servers-api';

/** SF-T-209: what people do to tasks' dev servers, and the Board's overview of them. */

/** The Board's overview looks again this often (machines report each minute). */
const OVERVIEW_POLL_MS = 60_000;


export type DevServerActionKind = 'start' | 'stop' | 'tidy';

/** Runs one action for a task and says how it went; true when the machine took it. */
export async function runDevServerAction(taskId: string, action: DevServerActionKind, project?: string): Promise<boolean> {
  if (action === 'tidy') {
    const ok = await confirmDialog({
      title: 'Tidy this worktree?',
      description:
        "Its dev servers stop, and the worktree, its ports, memory claim and database branch are removed from the machine. The branch stays on GitHub. The machine refuses if anything isn't committed and pushed.",
      confirmLabel: 'Tidy',
      destructive: true,
    });
    if (!ok) return false;
  }
  const call = action === 'start' ? startDevServers : action === 'stop' ? stopDevServers : tidyWorktree;
  const res: ApiResponse<unknown> = await call(taskId, { project });
  if (!res.data) {
    toast.error(describeApiError(res, `Couldn't ${action === 'tidy' ? 'tidy the worktree' : `${action} the dev servers`}`));
    return false;
  }
  toast.success(action === 'start' ? 'Starting the dev servers: the panel shows each step' : action === 'stop' ? 'Stopping the dev servers' : 'Tidying the worktree');
  return true;
}

/**
 * SF-T-209: the project's tasks with a worktree on a machine, and their dev servers, for the Board's button. Polls
 * every minute, and as the hub says one moved on (every 15 s while one is busy: SF-T-231); `reload` reads now (opening the dialog, after an action).
 */
export function useDevServersOverview(slug: string | null) {
  const [busy, setBusy] = useState(false);
  // SF-T-231: the hub says a task's dev servers moved on (or the socket came back): read them again.
  const pushed = useTaskStore((s) => s.devServersTick);
  const loader = useLoader(async (signal) => (slug ? listDevServers(null, { signal, project: slug }) : null), [slug, pushed], {
    enabled: Boolean(slug),
    pollMs: busy ? DEV_BUSY_POLL_MS : OVERVIEW_POLL_MS,
  });
  const res = loader.data;
  const list = res?.data?.dev_servers ?? null;
  const now = useNow(5_000, Boolean(list?.some((d) => devProgressAction(d))));
  const nowBusy = Boolean(list?.some((d) => d.action?.status === 'running' || devProgressPending(d, now)));
  if (nowBusy !== busy) setBusy(nowBusy);
  return {
    list: res?.data?.dev_servers ?? null,
    error: res && !res.data ? describeApiError(res) : null,
    reload: loader.reload,
    loading: loader.loading,
  };
}
